import React, { useEffect, useState, useCallback, useRef } from 'react';
import { API_BASE as API_URL } from '../lib/apiBase';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue
} from '../components/ui/select';
import { MultiSelect } from '../components/MultiSelect';
import { toast } from 'sonner';
import {
  Upload, FileSpreadsheet, Trash2, Pencil, Eye, Lock, Unlock, Loader2, Download, Save, Sparkles, AlertTriangle
} from 'lucide-react';

const authHeader = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const jsonHeaders = () => ({ ...authHeader(), 'Content-Type': 'application/json' });

const KIND_LABEL = { field: 'From employee data', formula: 'Formula (auto-calculated)', constant: 'Fixed value' };
const KIND_COLOR = { field: 'bg-emerald-100 text-emerald-700', formula: 'bg-indigo-100 text-indigo-700', constant: 'bg-slate-100 text-slate-600' };

const money = (v) => {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'number') return v.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  return String(v);
};

export default function ExcelSalaryTemplates() {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [meta, setMeta] = useState({ fields: [], appointment_types: [], categories: [], departments: [], payroll_months: [] });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [tab, setTab] = useState('manage');
  const [sheetMonth, setSheetMonth] = useState('');
  const [sheetMode, setSheetMode] = useState('payroll');
  const [sheetData, setSheetData] = useState(null);
  const [sheetLoading, setSheetLoading] = useState(false);

  const loadSheets = useCallback(async (month, mode) => {
    if (!month) { setSheetData(null); return; }
    setSheetLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/salary-excel-templates/payroll-view?month=${encodeURIComponent(month)}&mode=${mode}`,
        { headers: jsonHeaders(), credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed to load sheets');
      setSheetData(d);
    } catch (e) { toast.error(e.message); setSheetData(null); } finally { setSheetLoading(false); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, mRes] = await Promise.all([
        fetch(`${API_URL}/api/salary-excel-templates`, { headers: jsonHeaders(), credentials: 'include' }),
        fetch(`${API_URL}/api/salary-excel-templates/meta`, { headers: jsonHeaders(), credentials: 'include' }),
      ]);
      if (!tRes.ok) throw new Error('Failed to load templates');
      const t = await tRes.json();
      const m = mRes.ok ? await mRes.json() : null;
      setTemplates(Array.isArray(t) ? t : []);
      if (m && !m.detail) setMeta(m);
    } catch (e) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ---- upload ----
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file) { e.target.value = ''; }
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/salary-excel-templates/upload`, {
        method: 'POST', headers: authHeader(), credentials: 'include', body: fd,
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Upload failed');
      toast.success(`Uploaded — ${d.notes?.fields_detected ?? 0} fields and ${d.notes?.formulas_detected ?? 0} formulas detected`);
      await load();
      openEditor(d.template);
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // ---- editor ----
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [liveEmp, setLiveEmp] = useState('');
  const [liveVals, setLiveVals] = useState(null);
  const [liveBusy, setLiveBusy] = useState(false);

  const openEditor = (t) => {
    setEditing(t);
    setLiveEmp(''); setLiveVals(null); setEmpQuery('');
    fetchSampleEmps('');
    setForm({
      name: t.name || '',
      description: t.description || '',
      priority: t.priority || 0,
      applies_to: {
        appointment_types: (t.applies_to?.appointment_types) || [],
        categories: (t.applies_to?.categories) || [],
        departments: (t.applies_to?.departments) || [],
      },
      columns: (t.columns || []).map(c => ({ ...c })),
    });
  };
  const closeEditor = () => { setEditing(null); setForm(null); setLiveEmp(''); setLiveVals(null); };

  const runLivePreview = async (empId) => {
    setLiveEmp(empId);
    if (!empId || !editing || !form) { setLiveVals(null); return; }
    setLiveBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/salary-excel-templates/${editing.template_id}/preview-draft`, {
        method: 'POST', headers: jsonHeaders(), credentials: 'include',
        body: JSON.stringify({ employee_id: empId, columns: form.columns.map((c, i) => ({
          index: c.index ?? i + 1, col_letter: c.col_letter, header: c.header, kind: c.kind,
          field_key: c.kind === 'field' ? c.field_key : null, formula: c.formula || null,
          constant: c.constant ?? null, role: c.role || null,
        })) }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Preview failed');
      setLiveVals(d.values || {});
    } catch (e) { toast.error(e.message); setLiveVals(null); } finally { setLiveBusy(false); }
  };

  const setCol = (i, patch) => setForm(f => ({ ...f, columns: f.columns.map((c, idx) => idx === i ? { ...c, ...patch } : c) }));

  // Selecting every option (MultiSelect "All") means "no filter" → store [].
  const setApplies = (key, vals, options) => {
    const next = (vals.length === (options || []).length) ? [] : vals;
    setForm(f => ({ ...f, applies_to: { ...f.applies_to, [key]: next } }));
  };

  const saveEditor = async () => {
    setSaving(true);
    const wasLocked = !!editing.locked;
    try {
      const body = {
        name: form.name.trim(), description: form.description,
        entity_ids: editing.entity_ids || [], priority: Number(form.priority) || 0,
        applies_to: form.applies_to,
        columns: form.columns.map((c, i) => ({
          index: c.index ?? i + 1, col_letter: c.col_letter, header: c.header,
          kind: c.kind, field_key: c.kind === 'field' ? c.field_key : null,
          formula: c.formula || null, constant: c.constant ?? null, sample: c.sample ?? null,
          role: c.role || null,
        })),
      };
      if (wasLocked) {
        await fetch(`${API_URL}/api/salary-excel-templates/${editing.template_id}/unlock`, { method: 'POST', headers: jsonHeaders(), credentials: 'include' });
      }
      const res = await fetch(`${API_URL}/api/salary-excel-templates/${editing.template_id}`, {
        method: 'PUT', headers: jsonHeaders(), credentials: 'include', body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Save failed');
      if (wasLocked) {
        await fetch(`${API_URL}/api/salary-excel-templates/${editing.template_id}/lock`, { method: 'POST', headers: jsonHeaders(), credentials: 'include' });
      }
      toast.success(wasLocked ? 'Template saved (re-locked)' : 'Template saved');
      closeEditor();
      load();
    } catch (e) {
      toast.error(e.message || 'Save failed');
      if (wasLocked) {
        try { await fetch(`${API_URL}/api/salary-excel-templates/${editing.template_id}/lock`, { method: 'POST', headers: jsonHeaders(), credentials: 'include' }); } catch { /* keep going */ }
      }
    } finally {
      setSaving(false);
    }
  };

  const removeTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/salary-excel-templates/${t.template_id}`, {
        method: 'DELETE', headers: jsonHeaders(), credentials: 'include',
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Delete failed'); }
      toast.success('Template deleted');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const [lockWarn, setLockWarn] = useState(null);
  const toggleLock = async (t) => {
    const action = t.locked ? 'unlock' : 'lock';
    try {
      const res = await fetch(`${API_URL}/api/salary-excel-templates/${t.template_id}/${action}`, {
        method: 'POST', headers: jsonHeaders(), credentials: 'include',
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      if (action === 'lock' && d.warnings && d.warnings.length) {
        setLockWarn({ name: t.name, warnings: d.warnings });
      } else {
        toast.success(d.message || 'Done');
      }
      load();
    } catch (e) { toast.error(e.message); }
  };

  // ---- preview ----
  const [previewFor, setPreviewFor] = useState(null);
  const [sampleEmps, setSampleEmps] = useState([]);
  const [empQuery, setEmpQuery] = useState('');
  const [previewEmp, setPreviewEmp] = useState('');
  const [previewRow, setPreviewRow] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchSampleEmps = useCallback(async (q = '') => {
    try {
      const qs = q ? `?q=${encodeURIComponent(q)}` : '';
      const res = await fetch(`${API_URL}/api/salary-templates/sample-employees${qs}`, { headers: jsonHeaders(), credentials: 'include' });
      const d = await res.json();
      setSampleEmps(Array.isArray(d) ? d : []);
    } catch { setSampleEmps([]); }
  }, []);

  useEffect(() => {
    if (!previewFor) return;
    const t = setTimeout(() => fetchSampleEmps(empQuery), 300);
    return () => clearTimeout(t);
  }, [empQuery, previewFor, fetchSampleEmps]);

  const openPreview = (t) => {
    setPreviewFor(t); setPreviewRow(null); setPreviewEmp(''); setEmpQuery('');
    fetchSampleEmps('');
  };

  const runPreview = async (empId) => {
    setPreviewEmp(empId);
    if (!empId) { setPreviewRow(null); return; }
    setPreviewLoading(true); setPreviewRow(null);
    try {
      const res = await fetch(`${API_URL}/api/salary-excel-templates/${previewFor.template_id}/preview`, {
        method: 'POST', headers: jsonHeaders(), credentials: 'include',
        body: JSON.stringify({ employee_id: empId }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Preview failed');
      setPreviewRow(d);
    } catch (e) { toast.error(e.message); } finally { setPreviewLoading(false); }
  };

  // ---- generate ----
  const [genMonth, setGenMonth] = useState({});
  const [genBusy, setGenBusy] = useState(null);
  const generate = async (t) => {
    const month = genMonth[t.template_id] || (meta.payroll_months || [])[0];
    if (!month) { toast.error('No generated payroll month available yet'); return; }
    await downloadTemplateMonth(t.template_id, t.name, month);
  };

  const downloadTemplateMonth = async (templateId, name, month) => {
    setGenBusy(templateId);
    try {
      const res = await fetch(`${API_URL}/api/salary-excel-templates/${templateId}/generate`, {
        method: 'POST', headers: jsonHeaders(), credentials: 'include', body: JSON.stringify({ month }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || `Failed (HTTP ${res.status})`); }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(name || 'salary').replace(/[^a-zA-Z0-9]+/g, '_')}_${month}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
      toast.success('Salary file downloaded');
    } catch (e) { toast.error(e.message); } finally { setGenBusy(null); }
  };

  const appliesSummary = (t) => {
    const a = t.applies_to || {};
    const parts = [];
    if (a.appointment_types?.length) parts.push(a.appointment_types.join(', '));
    if (a.categories?.length) parts.push(a.categories.join(', '));
    if (a.departments?.length) parts.push(a.departments.join(', '));
    return parts.length ? parts.join(' · ') : 'All staff';
  };

  const numFields = (meta.fields || []).filter(f => f.kind === 'number');
  const textFields = (meta.fields || []).filter(f => f.kind === 'text');

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="excel-salary-templates-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <FileSpreadsheet className="w-7 h-7 text-emerald-600" /> Salary Templates
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Upload your own salary structure as an Excel table (one header row + one sample employee row,
            with formulas where needed). Values are pulled from each employee's master data, your formulas
            recalculate, and the finished salary sheet downloads after payroll is generated.
          </p>
        </div>
        {tab === 'manage' && (
        <div>
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={onUpload} data-testid="upload-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-template-btn"
            className="bg-emerald-600 hover:bg-emerald-700">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Upload Excel Template
          </Button>
        </div>
        )}
      </div>

      <div className="flex gap-1 mb-5 border-b">
        <button onClick={() => setTab('manage')} data-testid="tab-manage"
          className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === 'manage' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>Manage Templates</button>
        <button onClick={() => { setTab('sheets'); const m = sheetMonth || (meta.payroll_months || [])[0] || ''; if (m) { setSheetMonth(m); loadSheets(m, sheetMode); } }} data-testid="tab-sheets"
          className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === 'sheets' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>Payroll Sheets</button>
      </div>

      {tab === 'manage' && (loading ? (
        <div className="flex items-center gap-2 text-slate-400 py-20 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : templates.length === 0 ? (
        <div className="border-2 border-dashed rounded-xl p-12 text-center text-slate-400" data-testid="empty-state">
          <FileSpreadsheet className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium text-slate-500">No Excel templates yet</p>
          <p className="text-sm mt-1">Upload an .xlsx salary structure to get started.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {templates.map(t => (
            <div key={t.template_id} className="border rounded-xl bg-white p-4 shadow-sm" data-testid={`template-card-${t.template_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900 truncate">{t.name}</h3>
                    {t.locked
                      ? <Badge className="bg-amber-100 text-amber-700" data-testid={`locked-badge-${t.template_id}`}><Lock className="w-3 h-3 mr-1" />Locked</Badge>
                      : <Badge variant="outline" className="text-slate-500">Draft</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {(t.columns || []).length} columns · Priority {t.priority || 0}
                  </p>
                  <p className="text-xs text-slate-600 mt-1"><span className="text-slate-400">Applies to:</span> {appliesSummary(t)}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                <Button size="sm" variant="outline" onClick={() => openPreview(t)} data-testid={`preview-btn-${t.template_id}`}>
                  <Eye className="w-4 h-4 mr-1" /> Preview
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEditor(t)} data-testid={`edit-btn-${t.template_id}`}>
                  <Pencil className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => toggleLock(t)} data-testid={`lock-btn-${t.template_id}`}>
                  {t.locked ? <><Unlock className="w-4 h-4 mr-1" /> Unlock</> : <><Lock className="w-4 h-4 mr-1" /> Finalize & Lock</>}
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => removeTemplate(t)} data-testid={`delete-btn-${t.template_id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {t.locked && (
                <div className="mt-3 pt-3 border-t flex flex-wrap items-center gap-2" data-testid={`generate-row-${t.template_id}`}>
                  <span className="text-xs text-slate-500">Download salary file for</span>
                  <Select value={genMonth[t.template_id] || (meta.payroll_months || [])[0] || ''}
                    onValueChange={(v) => setGenMonth(m => ({ ...m, [t.template_id]: v }))}>
                    <SelectTrigger className="h-8 w-[130px]" data-testid={`gen-month-${t.template_id}`}><SelectValue placeholder="Month" /></SelectTrigger>
                    <SelectContent>
                      {(meta.payroll_months || []).length === 0 && <SelectItem value="__none" disabled>No payroll yet</SelectItem>}
                      {(meta.payroll_months || []).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={() => generate(t)} disabled={genBusy === t.template_id || !(meta.payroll_months || []).length}
                    className="bg-indigo-600 hover:bg-indigo-700" data-testid={`download-btn-${t.template_id}`}>
                    {genBusy === t.template_id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                    Download
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      ))}

      {tab === 'sheets' && (
        <div data-testid="sheets-view">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4 border rounded-lg bg-slate-50 px-3 py-2.5">
            <div className="flex items-center gap-2">
              <Label className="text-xs font-semibold text-slate-700 whitespace-nowrap">Month</Label>
              <Select value={sheetMonth} onValueChange={(v) => { setSheetMonth(v); loadSheets(v, sheetMode); }}>
                <SelectTrigger className="h-9 w-[140px] bg-white" data-testid="sheet-month"><SelectValue placeholder="Pick month" /></SelectTrigger>
                <SelectContent>
                  {(meta.payroll_months || []).length === 0 && <SelectItem value="__none" disabled>No payroll yet</SelectItem>}
                  {(meta.payroll_months || []).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1 bg-white rounded-md border p-0.5" data-testid="sheet-mode-toggle">
              <button onClick={() => { setSheetMode('payroll'); if (sheetMonth) loadSheets(sheetMonth, 'payroll'); }}
                className={`px-3 py-1.5 text-xs rounded ${sheetMode === 'payroll' ? 'bg-emerald-600 text-white' : 'text-slate-600'}`} data-testid="sheet-mode-payroll">As generated</button>
              <button onClick={() => { setSheetMode('live'); if (sheetMonth) loadSheets(sheetMonth, 'live'); }}
                className={`px-3 py-1.5 text-xs rounded ${sheetMode === 'live' ? 'bg-emerald-600 text-white' : 'text-slate-600'}`} data-testid="sheet-mode-live">Live (master)</button>
            </div>
            <p className="text-[11px] text-slate-400 sm:ml-auto">
              {sheetMode === 'payroll' ? "Values from that month's generated payroll" : 'Values recalculated live from master data'}
            </p>
          </div>

          {sheetLoading ? (
            <div className="flex items-center gap-2 text-slate-400 py-16 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading sheets…</div>
          ) : !sheetData ? (
            <div className="text-center text-slate-400 py-16 text-sm">Pick a month to view its template-wise salary sheets.</div>
          ) : sheetData.templates.length === 0 ? (
            <div className="border-2 border-dashed rounded-xl p-10 text-center text-slate-400" data-testid="sheets-empty">
              <p className="font-medium text-slate-500">No template-covered staff for {sheetData.month}</p>
              <p className="text-sm mt-1">{sheetData.total_payroll} staff have payroll this month, but none are claimed by a locked template.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {sheetData.untemplated_count > 0 && (
                <p className="text-xs text-slate-500" data-testid="untemplated-note">
                  {sheetData.untemplated_count} other staff aren't covered by any template — they stay in the normal payroll sheet.
                </p>
              )}
              {sheetData.templates.map(t => (
                <div key={t.template_id} className="border rounded-xl bg-white overflow-hidden" data-testid={`sheet-${t.template_id}`}>
                  <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-slate-50 border-b">
                    <div>
                      <h3 className="font-semibold text-slate-800">{t.name}</h3>
                      <span className="text-xs text-slate-500">{t.count} staff · Priority {t.priority}</span>
                    </div>
                    <Button size="sm" onClick={() => downloadTemplateMonth(t.template_id, t.name, sheetData.month)}
                      disabled={genBusy === t.template_id} className="bg-indigo-600 hover:bg-indigo-700" data-testid={`sheet-download-${t.template_id}`}>
                      {genBusy === t.template_id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Download
                    </Button>
                  </div>
                  {sheetMode === 'payroll' && t.rows.some(r => r.generated === false) && (
                    <p className="px-4 py-2 text-[11px] text-amber-700 bg-amber-50 border-b" data-testid={`sheet-ungenerated-note-${t.template_id}`}>
                      Some staff have no generated payroll for {sheetData.month} yet — showing full-month figures. Generate payroll in the Payroll module so attendance (days) is applied.
                    </p>
                  )}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-100 text-slate-600">
                          {t.columns.map(c => <th key={c.col_letter} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{c.header}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {t.rows.map(r => (
                          <tr key={r.employee_id} className={`border-t ${sheetMode === 'payroll' && r.generated === false ? 'bg-amber-50/60' : ''}`} data-testid={`sheet-row-${r.employee_id}`}>
                            {t.columns.map((c, ci) => (
                              <td key={c.col_letter} className={`px-3 py-2 whitespace-nowrap ${c.kind === 'formula' ? 'text-indigo-700 font-medium' : 'text-slate-700'}`}>
                                {ci === 0 ? (
                                  <span className="inline-flex items-center gap-2">
                                    {money(r.values[c.col_letter])}
                                    {sheetMode === 'payroll' && r.generated === false && (
                                      <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0" data-testid={`sheet-ungenerated-badge-${r.employee_id}`}>Not generated</Badge>
                                    )}
                                  </span>
                                ) : money(r.values[c.col_letter])}
                              </td>
                            ))}
                          </tr>
                        ))}
                        <tr className="border-t-2 bg-slate-50 font-semibold text-slate-800" data-testid={`sheet-total-${t.template_id}`}>
                          {t.columns.map((c, i) => (
                            <td key={c.col_letter} className="px-3 py-2 whitespace-nowrap">
                              {i === 0 ? 'TOTAL' : (t.totals[c.col_letter] !== undefined ? money(t.totals[c.col_letter]) : '')}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------- editor dialog ---------- */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && closeEditor()}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="editor-dialog">
          <DialogHeader>
            <DialogTitle>Configure template</DialogTitle>
            <DialogDescription>Check the auto-detected column mapping, choose who it applies to, then save.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Template name</Label>
                  <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="editor-name" />
                </div>
                <div>
                  <Label className="text-xs">Priority (higher wins on overlap)</Label>
                  <Input type="number" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} data-testid="editor-priority" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="editor-description" />
              </div>

              <div className="rounded-lg border bg-slate-50 p-3">
                <p className="text-sm font-semibold text-slate-700 mb-2">Apply this template to</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[11px] text-slate-500">Appointment type</Label>
                    <MultiSelect options={meta.appointment_types} selected={form.applies_to.appointment_types}
                      onChange={(v) => setApplies('appointment_types', v, meta.appointment_types)}
                      allLabel="All" emptyLabel="All" testId="applies-appointment" />
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-500">Category</Label>
                    <MultiSelect options={meta.categories} selected={form.applies_to.categories}
                      onChange={(v) => setApplies('categories', v, meta.categories)}
                      allLabel="All" emptyLabel="All" testId="applies-category" />
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-500">Department</Label>
                    <MultiSelect options={meta.departments} selected={form.applies_to.departments}
                      onChange={(v) => setApplies('departments', v, meta.departments)}
                      allLabel="All" emptyLabel="All" testId="applies-department" />
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-2">Leave a box as "All" to not filter on it. Empty = applies to everyone.</p>
              </div>

              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                  <p className="text-sm font-semibold text-slate-700">Columns ({form.columns.length})</p>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-slate-500">Live example:</span>
                    <Select value={liveEmp} onValueChange={runLivePreview}>
                      <SelectTrigger className="h-8 w-[200px] text-xs" data-testid="live-preview-emp"><SelectValue placeholder="Pick an employee…" /></SelectTrigger>
                      <SelectContent>
                        {sampleEmps.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name} · {e.employee_id}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {liveEmp && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => runLivePreview(liveEmp)} disabled={liveBusy} data-testid="live-preview-refresh">{liveBusy ? 'Computing…' : 'Recompute'}</Button>}
                  </div>
                </div>
                {liveEmp && <p className="text-[11px] text-indigo-600 mb-2" data-testid="live-preview-hint">Numbers on the right show this employee computed with your current formulas — edit a formula and Recompute to verify before saving.</p>}
                <div className="space-y-2">
                  {form.columns.map((c, i) => (
                    <div key={i} className="rounded-lg border bg-white p-2.5 flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-2" data-testid={`col-row-${i}`}>
                      <div className="sm:w-40 min-w-0">
                        <Input value={c.header} onChange={e => setCol(i, { header: e.target.value })} className="h-8 text-sm font-medium" data-testid={`col-header-${i}`} />
                        <span className="text-[10px] text-slate-400">cell {c.col_letter} · e.g. {money(c.sample)}</span>
                      </div>
                      <Select value={c.kind} onValueChange={(v) => setCol(i, { kind: v })}>
                        <SelectTrigger className="h-8 sm:w-[150px] text-xs" data-testid={`col-kind-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="field">{KIND_LABEL.field}</SelectItem>
                          <SelectItem value="formula">{KIND_LABEL.formula}</SelectItem>
                          <SelectItem value="constant">{KIND_LABEL.constant}</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex-1 min-w-0">
                        {c.kind === 'field' && (
                          <Select value={c.field_key || ''} onValueChange={(v) => setCol(i, { field_key: v })}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`col-field-${i}`}><SelectValue placeholder="Pick employee field…" /></SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                <SelectLabel>Details</SelectLabel>
                                {textFields.map(f => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                              </SelectGroup>
                              <SelectGroup>
                                <SelectLabel>Amounts</SelectLabel>
                                {numFields.map(f => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        )}
                        {c.kind === 'formula' && (
                          <Input value={c.formula || ''} onChange={e => setCol(i, { formula: e.target.value })}
                            className="h-8 text-[11px] font-mono bg-indigo-50 text-indigo-700 border-indigo-200"
                            placeholder="=D2+E2+F2" data-testid={`col-formula-${i}`} />
                        )}
                        {c.kind === 'constant' && (
                          <Input value={c.constant ?? ''} onChange={e => setCol(i, { constant: e.target.value })} className="h-8 text-xs" placeholder="Fixed value" data-testid={`col-constant-${i}`} />
                        )}
                      </div>
                      {c.kind !== 'field' || numFields.some(f => f.key === c.field_key) ? (
                        <Select value={c.role || '__none'} onValueChange={(v) => setCol(i, { role: v === '__none' ? null : v })}>
                          <SelectTrigger className="h-8 sm:w-[128px] text-xs" data-testid={`col-role-${i}`}><SelectValue placeholder="Maps to…" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none">— no mapping —</SelectItem>
                            <SelectItem value="basic">Basic</SelectItem>
                            <SelectItem value="da">DA</SelectItem>
                            <SelectItem value="hra">HRA</SelectItem>
                            <SelectItem value="gross">Gross</SelectItem>
                            <SelectItem value="deductions">Deductions</SelectItem>
                            <SelectItem value="net">Net</SelectItem>
                            <SelectItem value="present_days">Present days</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : <div className="sm:w-[128px]" />}
                      {liveEmp ? (
                        <span className="sm:w-24 text-right text-xs font-semibold text-slate-700 tabular-nums shrink-0" data-testid={`col-live-${i}`}>
                          {liveVals && c.col_letter in liveVals ? (typeof liveVals[c.col_letter] === 'number' ? money(liveVals[c.col_letter]) : String(liveVals[c.col_letter] ?? '—')) : '—'}
                        </span>
                      ) : null}
                      <Badge className={`${KIND_COLOR[c.kind]} shrink-0`}>{c.kind}</Badge>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 mt-2">Tip: "Maps to" lets a formula column (e.g. Gross/Net) feed the Payroll records, stat cards and reports so every screen shows the same number as this sheet.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeEditor}>Cancel</Button>
            <Button onClick={saveEditor} disabled={saving} data-testid="editor-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- preview dialog ---------- */}
      <Dialog open={!!previewFor} onOpenChange={(o) => !o && setPreviewFor(null)}>
        <DialogContent className="sm:max-w-4xl" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-emerald-600" /> {previewFor?.name}</DialogTitle>
            <DialogDescription>Pick a real staff member — the template fills in their master salary and recalculates every formula so you can confirm it before locking.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 border rounded-lg bg-slate-50 px-3 py-2">
            <Label className="text-xs font-semibold text-slate-700 whitespace-nowrap">Preview with</Label>
            <Input className="bg-white h-9 sm:max-w-[200px]" placeholder="Search staff by name…" value={empQuery} onChange={e => setEmpQuery(e.target.value)} data-testid="preview-emp-search" />
            <Select value={previewEmp} onValueChange={runPreview}>
              <SelectTrigger className="bg-white h-9 sm:max-w-[300px]" data-testid="preview-emp-select"><SelectValue placeholder="Select an employee" /></SelectTrigger>
              <SelectContent>
                {sampleEmps.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}{e.designation ? ` — ${e.designation}` : ''}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {previewLoading ? (
            <div className="flex items-center gap-2 text-slate-400 py-12 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Calculating…</div>
          ) : !previewRow ? (
            <div className="text-center text-slate-400 py-12 text-sm">Select an employee to see their salary sheet.</div>
          ) : (
            <div className="overflow-x-auto border rounded-lg" data-testid="preview-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100 text-slate-600">
                    {previewRow.columns.map(c => (
                      <th key={c.col_letter} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{c.header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t" data-testid="preview-data-row">
                    {previewRow.columns.map(c => (
                      <td key={c.col_letter} className={`px-3 py-2 whitespace-nowrap ${c.kind === 'formula' ? 'text-indigo-700 font-medium' : 'text-slate-700'}`}>
                        {money(previewRow.values[c.col_letter])}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewFor(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- lock overlap warning ---------- */}
      <Dialog open={!!lockWarn} onOpenChange={(o) => !o && setLockWarn(null)}>
        <DialogContent className="max-w-lg" data-testid="lock-warning-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600"><AlertTriangle className="w-5 h-5" /> Locked — overlap found</DialogTitle>
            <DialogDescription>
              Some staff also match another locked template. When you download, the template with the higher <b>Priority</b> number wins for those staff.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto text-sm space-y-1">
            {(lockWarn?.warnings || []).map((w, i) => (
              <div key={i} className="flex justify-between gap-2 border-b py-1">
                <span className="text-slate-700">{w.employee}</span>
                <span className="text-slate-400 text-xs">also in: {w.also_in.join(', ')}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setLockWarn(null)}>Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
